import React from 'react';
import { Stethoscope, Activity, Baby, TestTube, Eye, HeartPulse, Syringe, Users, Scissors, Dna, BriefcaseMedical, BrainCircuit, PersonStanding, HeartHandshake } from 'lucide-react';
export interface ServiceData {
  id: string;
  slug: string;
  title: string;
  shortDesc: string;
  icon: React.ReactNode;
  overview: string;
  benefits: string[];
  process: {
    title: string;
    desc: string;
  }[];
  faqs: {
    q: string;
    a: string;
  }[];
}
const generateGenericProcess = (serviceName: string) => [{
  title: 'Initial Consultation',
  desc: `Discuss your medical history and specific needs regarding ${serviceName.toLowerCase()}.`
}, {
  title: 'Assessment & Diagnosis',
  desc: 'Our specialists perform a thorough examination using modern diagnostic tools.'
}, {
  title: 'Treatment Plan',
  desc: 'We develop a personalized care plan tailored to your health goals.'
}, {
  title: 'Follow-up Care',
  desc: 'Ongoing monitoring and support to ensure optimal health outcomes.'
}];
const generateGenericFaqs = (serviceName: string) => [{
  q: `Do I need an appointment for ${serviceName.toLowerCase()}?`,
  a: 'While we accept walk-ins, we highly recommend booking an appointment to minimize your wait time.'
}, {
  q: 'Is this service covered by medical aid?',
  a: 'We accept most major medical aid providers. Please check with our reception for specific coverage details.'
}, {
  q: 'How long does a typical session take?',
  a: 'Most sessions take between 30 to 60 minutes, depending on the complexity of your case.'
}];
export const SERVICES_DATA: ServiceData[] = [{
  id: 'general-consultations',
  slug: 'general-medical-consultations',
  title: 'General Medical Consultations',
  shortDesc: 'Expert medical advice and treatment for common illnesses and health concerns.',
  icon: <Stethoscope className="w-6 h-6" data-id="element-358" />,
  overview: "Our general medical consultations provide comprehensive primary care for patients of all ages. Whether you're dealing with a sudden illness, managing a chronic condition, or seeking preventive care, our experienced doctors are here to listen, diagnose, and treat you with compassion.",
  benefits: ['Comprehensive health assessments', 'Personalized treatment plans', 'Preventive care and health education', 'Referrals to specialists when necessary'],
  process: generateGenericProcess('General Consultations'),
  faqs: generateGenericFaqs('General Consultations')
}, {
  id: 'maternal-care',
  slug: 'antenatal-care',
  title: 'Antenatal Care',
  shortDesc: 'Comprehensive care and monitoring for expectant mothers.',
  icon: <Baby className="w-6 h-6" data-id="element-359" />,
  overview: 'Our antenatal care services are designed to ensure the health and well-being of both mother and baby throughout pregnancy. We provide regular check-ups, ultrasound scans, nutritional advice, and emotional support to prepare you for a safe delivery.',
  benefits: ['Regular health monitoring for mother and baby', 'Early detection of potential complications', 'Nutritional and lifestyle guidance', 'Birth preparedness planning'],
  process: [{
    title: 'First Trimester Booking',
    desc: 'Comprehensive initial assessment, blood tests, and dating scan.'
  }, {
    title: 'Routine Check-ups',
    desc: 'Regular visits to monitor blood pressure, fetal growth, and heartbeat.'
  }, {
    title: 'Anomaly Scan',
    desc: 'Detailed ultrasound at 20-22 weeks to check fetal development.'
  }, {
    title: 'Birth Preparation',
    desc: 'Discussing birth plans and signs of labor in the third trimester.'
  }],
  faqs: [{
    q: 'When should I start antenatal classes?',
    a: 'We recommend starting your antenatal visits as soon as you find out you are pregnant, ideally before 12 weeks.'
  }, {
    q: 'Are ultrasound scans included?',
    a: 'Yes, routine ultrasound scans are part of our comprehensive antenatal care package.'
  }, {
    q: 'Can my partner attend the appointments?',
    a: 'Absolutely. We encourage partners to be involved throughout the pregnancy journey.'
  }]
}, {
  id: 'laboratory-services',
  slug: 'laboratory-services',
  title: 'Laboratory Services',
  shortDesc: 'Accurate and fast diagnostic testing including blood work.',
  icon: <TestTube className="w-6 h-6" data-id="element-360" />,
  overview: 'Maisha Medical Center features a state-of-the-art laboratory equipped to perform a wide range of diagnostic tests. Our fast turnaround times ensure that your doctor gets the results needed to make accurate diagnoses and timely treatment decisions.',
  benefits: ['Fast and accurate results', 'Wide range of testing capabilities', 'Strict quality control standards', 'Convenient on-site testing'],
  process: [{
    title: 'Sample Collection',
    desc: 'Our trained phlebotomists safely and comfortably collect your samples.'
  }, {
    title: 'Analysis',
    desc: 'Samples are processed using advanced automated analyzers.'
  }, {
    title: 'Quality Review',
    desc: 'Results are verified by our senior laboratory technicians.'
  }, {
    title: 'Result Delivery',
    desc: 'Results are sent directly to your consulting doctor for review.'
  }],
  faqs: [{
    q: 'Do I need to fast before my blood test?',
    a: 'Some tests require fasting for 8-12 hours. Your doctor will inform you if fasting is necessary for your specific tests.'
  }, {
    q: 'How long do results take?',
    a: 'Many routine tests are available within hours. Specialized tests may take 24-48 hours.'
  }, {
    q: 'Can I get a copy of my results?',
    a: 'Yes, you can request a copy of your results from the reception after your doctor has reviewed them with you.'
  }]
}, {
  id: 'ultrasound',
  slug: 'ultrasound-services',
  title: 'Ultrasound Services',
  shortDesc: 'Advanced imaging for precise medical diagnosis.',
  icon: <Activity className="w-6 h-6" data-id="element-361" />,
  overview: 'We offer advanced diagnostic ultrasound services for obstetrics, gynecology, abdominal, and soft tissue imaging. Our modern equipment provides high-resolution images that help our doctors make accurate and timely diagnoses.',
  benefits: ['Non-invasive and painless imaging', 'No radiation exposure', 'Real-time dynamic imaging', 'Immediate preliminary results'],
  process: generateGenericProcess('Ultrasound Services'),
  faqs: generateGenericFaqs('Ultrasound Services')
}, {
  id: 'minor-surgery',
  slug: 'minor-surgical-procedures',
  title: 'Minor Surgical Procedures',
  shortDesc: 'Safe, in-clinic surgical treatments for minor conditions.',
  icon: <Scissors className="w-6 h-6" data-id="element-362" />,
  overview: 'We perform a variety of minor surgical procedures in our fully equipped procedure rooms under local anesthesia. This includes wound suturing, removal of skin lesions, incision and drainage of abscesses, and more.',
  benefits: ['Performed in a sterile environment', 'Quick recovery times', 'Cost-effective compared to hospital admission', 'Expert surgical care'],
  process: generateGenericProcess('Minor Surgical Procedures'),
  faqs: generateGenericFaqs('Minor Surgical Procedures')
}, {
  id: 'circumcision',
  slug: 'safe-medical-circumcision',
  title: 'Safe Medical Circumcision',
  shortDesc: 'Professional and hygienic male circumcision services.',
  icon: <HeartHandshake className="w-6 h-6" data-id="element-363" />,
  overview: 'We provide safe, hygienic, and professional medical circumcision services for infants, boys, and adults. Our experienced medical team ensures the procedure is performed with minimal discomfort and provides comprehensive aftercare instructions.',
  benefits: ['Reduced risk of infections', 'Performed under sterile conditions', 'Comprehensive pain management', 'Detailed aftercare support'],
  process: generateGenericProcess('Medical Circumcision'),
  faqs: generateGenericFaqs('Medical Circumcision')
}, {
  id: 'dna-testing',
  slug: 'paternity-testing-dna',
  title: 'Paternity Testing (DNA)',
  shortDesc: 'Highly accurate and confidential DNA testing services.',
  icon: <Dna className="w-6 h-6" data-id="element-364" />,
  overview: 'Our DNA paternity testing services offer 99.99% accuracy in determining biological relationships. We guarantee strict confidentiality and handle all samples with the utmost professional care and legal compliance if required.',
  benefits: ['99.99% accuracy', 'Strict confidentiality', 'Painless cheek swab collection', 'Fast turnaround times'],
  process: generateGenericProcess('DNA Testing'),
  faqs: generateGenericFaqs('DNA Testing')
}, {
  id: 'immunizations',
  slug: 'immunizations',
  title: 'Immunizations',
  shortDesc: 'Essential vaccines for children and adults.',
  icon: <Syringe className="w-6 h-6" data-id="element-365" />,
  overview: 'Protect yourself and your family from preventable diseases with our comprehensive immunization services. We offer childhood vaccination schedules, travel vaccines, and seasonal flu shots.',
  benefits: ['Disease prevention', 'Safe and effective vaccines', 'Schedules tracked by our clinic', 'Travel health protection'],
  process: generateGenericProcess('Immunizations'),
  faqs: generateGenericFaqs('Immunizations')
}, {
  id: 'pre-employment',
  slug: 'pre-employment-medical-examinations',
  title: 'Pre-Employment Medicals',
  shortDesc: 'Comprehensive health screenings for new employees.',
  icon: <BriefcaseMedical className="w-6 h-6" data-id="element-366" />,
  overview: 'We offer thorough pre-employment medical examinations to ensure candidates are medically fit for their prospective roles. Our assessments can be tailored to meet specific industry requirements.',
  benefits: ['Detailed health reports', 'Fast processing for employers', 'Customizable screening panels', 'Occupational health compliance'],
  process: generateGenericProcess('Pre-Employment Medicals'),
  faqs: generateGenericFaqs('Pre-Employment Medicals')
}, {
  id: 'family-planning',
  slug: 'family-planning',
  title: 'Family Planning',
  shortDesc: 'Confidential counseling and contraceptive services.',
  icon: <Users className="w-6 h-6" data-id="element-367" />,
  overview: 'Our family planning clinic provides confidential advice and a wide range of contraceptive options to help you make informed decisions about your reproductive health and family sizing.',
  benefits: ['Wide range of contraceptive options', 'Confidential environment', 'Expert reproductive advice', 'Long-term and short-term solutions'],
  process: generateGenericProcess('Family Planning'),
  faqs: generateGenericFaqs('Family Planning')
}, {
  id: 'counseling',
  slug: 'counseling-services',
  title: 'Counseling Services',
  shortDesc: 'Professional mental health and emotional support.',
  icon: <BrainCircuit className="w-6 h-6" data-id="element-368" />,
  overview: 'Mental health is a crucial part of overall well-being. Our professional counselors offer a safe, confidential space to discuss and manage stress, anxiety, depression, grief, and relationship issues.',
  benefits: ['Safe, non-judgmental environment', 'Evidence-based therapies', 'Stress and anxiety management', 'Improved emotional well-being'],
  process: generateGenericProcess('Counseling Services'),
  faqs: generateGenericFaqs('Counseling Services')
}, {
  id: 'chronic-disease',
  slug: 'chronic-disease-management',
  title: 'Chronic Disease Management',
  shortDesc: 'Ongoing support for diabetes, hypertension, and more.',
  icon: <HeartPulse className="w-6 h-6" data-id="element-369" />,
  overview: 'Managing a chronic condition requires consistent care and lifestyle adjustments. We provide comprehensive management plans for conditions like diabetes, hypertension, asthma, and HIV, focusing on long-term health and quality of life.',
  benefits: ['Regular monitoring and adjustments', 'Medication management', 'Lifestyle and dietary counseling', 'Prevention of complications'],
  process: generateGenericProcess('Chronic Disease Management'),
  faqs: generateGenericFaqs('Chronic Disease Management')
}, {
  id: 'physiotherapy',
  slug: 'physiotherapy',
  title: 'Physiotherapy',
  shortDesc: 'Rehabilitation and pain management therapies.',
  icon: <PersonStanding className="w-6 h-6" data-id="element-370" />,
  overview: 'Our physiotherapy services aim to restore movement and function following injury, illness, or disability. We use manual therapy, exercise prescription, and electrotherapy to relieve pain and improve mobility.',
  benefits: ['Pain relief without medication', 'Improved mobility and strength', 'Post-surgical rehabilitation', 'Sports injury recovery'],
  process: generateGenericProcess('Physiotherapy'),
  faqs: generateGenericFaqs('Physiotherapy')
}, {
  id: 'optician',
  slug: 'optician-services',
  title: 'Optician Services',
  shortDesc: 'Professional eye examinations and vision care.',
  icon: <Eye className="w-6 h-6" data-id="element-371" />,
  overview: 'Comprehensive eye care services including vision testing, prescription of glasses and contact lenses, and screening for eye diseases such as glaucoma and cataracts.',
  benefits: ['Accurate vision correction', 'Early detection of eye diseases', 'Wide selection of frames', 'Expert optical advice'],
  process: generateGenericProcess('Optician Services'),
  faqs: generateGenericFaqs('Optician Services')
}, {
  id: 'postnatal-care',
  slug: 'postnatal-care',
  title: 'Postnatal Care',
  shortDesc: 'Essential healthcare for mothers and newborns after birth.',
  icon: <Baby className="w-6 h-6" data-id="element-372" />,
  overview: 'The period following birth is critical for both mother and baby. Our postnatal care includes physical check-ups, breastfeeding support, newborn assessments, and monitoring for postpartum depression.',
  benefits: ['Support for physical recovery', 'Breastfeeding guidance', 'Newborn health monitoring', 'Emotional and mental health support'],
  process: generateGenericProcess('Postnatal Care'),
  faqs: generateGenericFaqs('Postnatal Care')
}, {
  id: 'ecg',
  slug: 'ecg-services',
  title: 'ECG Services',
  shortDesc: 'Electrocardiogram testing for heart health assessment.',
  icon: <Activity className="w-6 h-6" data-id="element-373" />,
  overview: 'An Electrocardiogram (ECG) is a simple, painless test that records the electrical activity of your heart. It helps our doctors diagnose heart conditions, monitor existing heart disease, and ensure your heart is healthy.',
  benefits: ['Quick and painless procedure', 'Immediate results', 'Crucial for diagnosing arrhythmias', 'Non-invasive heart monitoring'],
  process: generateGenericProcess('ECG Services'),
  faqs: generateGenericFaqs('ECG Services')
}];